import { hexKey, hexesInRect } from "@west-marches/shared";
import {
  DEFAULT_OCEAN_TILE_SET_ID,
  TILE_CATALOG,
  getRandomTileIdFromTileSet,
  getTileSetById,
  isOceanTileSet,
  isTileSetEnabled,
  type TileSet,
} from "@west-marches/shared";
import type { HexData, MapBounds, MapState } from "@west-marches/shared";

function hasPaintableTiles(tileSet: TileSet | undefined): tileSet is TileSet {
  return Boolean(tileSet && isTileSetEnabled(tileSet) && tileSet.tileIds.length > 0);
}

function getPaintableTileSets(): TileSet[] {
  return TILE_CATALOG.filter((tileSet) => hasPaintableTiles(tileSet));
}

function normalizeTileSetLabel(label: string): string {
  return label.trim().toLowerCase();
}

function tileSetLabelMatches(tileSet: TileSet, candidates: string[]): boolean {
  const normalizedLabel = normalizeTileSetLabel(tileSet.label);
  return candidates.some(
    (candidate) => normalizedLabel === candidate || normalizedLabel.includes(candidate),
  );
}

function pickInitialTileSet(options: {
  preferredIds: string[];
  fallback?: (tileSet: TileSet) => boolean;
}): TileSet {
  for (const tileSetId of options.preferredIds) {
    const tileSet = getTileSetById(tileSetId);
    if (hasPaintableTiles(tileSet)) {
      return tileSet;
    }
  }

  const paintableTileSets = getPaintableTileSets();
  const fallbackMatch = options.fallback ? paintableTileSets.find(options.fallback) : undefined;
  if (fallbackMatch) {
    return fallbackMatch;
  }

  const firstTileSet = paintableTileSets[0];
  if (firstTileSet) {
    return firstTileSet;
  }

  throw new Error("Tile catalog does not contain any paintable tile sets.");
}

// Produces a "blank canvas" map: a screen-aligned rectangle of ocean hexes with
// no decorations, labels, or hand-painted features. Used as the starting point
// for new campaigns (when there is no DB-flagged starter to clone) and as the
// future "Reset map" target.
export function createInitialMap(bounds: MapBounds): MapState {
  const oceanGroup = pickInitialTileSet({
    preferredIds: [DEFAULT_OCEAN_TILE_SET_ID, "ocean-still"],
    fallback: (tileSet) =>
      isOceanTileSet(tileSet) || tileSetLabelMatches(tileSet, ["water", "ocean"]),
  });

  const hexes: Record<string, HexData> = {};
  for (const h of hexesInRect(bounds.halfCols, bounds.halfRows)) {
    hexes[hexKey(h)] = {
      q: h.q,
      r: h.r,
      tileSetId: oceanGroup.id,
      tileId: getRandomTileIdFromTileSet(oceanGroup),
    };
  }

  return {
    bounds: { halfCols: bounds.halfCols, halfRows: bounds.halfRows },
    hexes,
    decorations: {},
    textLabels: {},
    partyPosition: { q: 0, r: 0 },
  };
}
